import React, { useState } from 'react';
import { BENCHMARK_INDEX_GROUPS } from '../../lib/benchmarkIndexGroups';

/**
 * Benchmark-index multi-select for the report generation dialog.
 *
 * A shared field control shaped like ReportingCurrencyField / IrrMethodField:
 * it renders its own label and drops bare into a popup's options area. It
 * governs WHICH curated benchmark indices the generated report shows - each
 * checkbox is one BENCHMARK_INDEX_GROUPS entry, and the value is the list of
 * selected group ids the dialog stores as `selected_index_groups` on the report
 * request (see lib/benchmarkIndexGroups + lib/benchmarkIndexPref).
 *
 * Controlled: `value` is the selected group ids, `onChange(nextIds)` fires on
 * every toggle with the ids kept in BENCHMARK_INDEX_GROUPS order. The only
 * internal state is the open/closed disclosure.
 */
export default function BenchmarkIndexSelect({ value = [], onChange, darkMode = false }) {
  const [open, setOpen] = useState(false);
  const selected = new Set(value);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(BENCHMARK_INDEX_GROUPS.map((g) => g.id).filter((gid) => next.has(gid)));
  };

  const count = value.length;
  const summary = count === BENCHMARK_INDEX_GROUPS.length ? 'All benchmarks'
    : count === 0 ? 'No benchmarks'
      : `${count} benchmark${count === 1 ? '' : 's'}`;

  return (
    <div>
      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
        Benchmark indices
      </label>
      <div className="relative">
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className={`w-full text-left text-sm rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${
            darkMode ? 'bg-[#1a1a1a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'
          }`}
        >
          Benchmarks: {summary}
        </button>
        {open && (
          <div
            className={`absolute z-30 mt-1 w-full rounded-lg border p-2 shadow-xl ${
              darkMode ? 'bg-[#1c1c1c] border-white/10' : 'bg-white border-gray-200'
            }`}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {BENCHMARK_INDEX_GROUPS.map((g) => (
              <label
                key={g.id}
                className={`flex items-center gap-2 px-1 py-1 text-sm cursor-pointer ${
                  darkMode ? 'text-gray-200' : 'text-gray-800'
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(g.id)}
                  onChange={() => toggle(g.id)}
                  aria-label={g.label}
                  className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                />
                {g.label}
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
